import type { SaveVendorPaymentInput, VendorDue, VendorLedger } from '../api/vendorPayments';
import type { PaymentMethod, VendorDueKind } from '../generated/enums';

/**
 * The vendor payment screen's rules (step 4 of `docs/Purchase_Sale_Outstanding_Design.md`). Oldest
 * first is the server's default and the preview here follows the same order — money already on
 * account first, then this payment — so what the screen shows is what saving does. Choosing amounts
 * by hand sends them; whatever is left stays on account.
 */

export interface PaymentDraft {
  vendorId: number | null;
  /** `YYYY-MM-DD`. */
  paidOn: string;
  amount: number | null;
  method: PaymentMethod;
  reference: string;
  notes: string;
  /** Off: oldest first. On: the amounts in {@link chosen}. */
  choosing: boolean;
  /** `kind:id` → amount, while choosing. */
  chosen: Record<string, number>;
  /** Paid from; null is the branch's default for the method. See `cash/paidFrom.ts`. */
  cashAccountId: number | null;
}

/** Ways money leaves the shop for a vendor — never an adjustment, which moves none. */
export const paymentMethods: readonly PaymentMethod[] = [
  'Cash',
  'Upi',
  'BankTransfer',
  'Cheque',
  'Card',
];

export const dueKey = (kind: VendorDueKind, id: number) => `${kind}:${id}`;

export function emptyPayment(today: string): PaymentDraft {
  return {
    vendorId: null,
    paidOn: today,
    amount: null,
    method: 'BankTransfer',
    reference: '',
    notes: '',
    choosing: false,
    chosen: {},
    cashAccountId: null,
  };
}

export interface PreviewLine {
  due: VendorDue;
  amount: number;
}

const round = (value: number) => Math.round(value * 100) / 100;

/**
 * What this payment will settle: oldest due first, with what is on account used before it. Returns
 * the lines it touches and what will be left on account afterwards.
 */
export function oldestFirst(
  ledger: Pick<VendorLedger, 'dues' | 'onAccount'>,
  amount: number,
): { lines: PreviewLine[]; leftOnAccount: number } {
  let money = round(ledger.onAccount + Math.max(0, amount));
  const lines: PreviewLine[] = [];

  for (const due of ledger.dues) {
    if (money <= 0) {
      break;
    }

    const part = round(Math.min(due.outstanding, money));

    if (part > 0) {
      lines.push({ due, amount: part });
      money = round(money - part);
    }
  }

  return { lines, leftOnAccount: money };
}

/** The shop's own split, summed — what it sets against dues, the rest going on account. */
export function chosenTotal(draft: PaymentDraft): number {
  return round(Object.values(draft.chosen).reduce((sum, value) => sum + (value || 0), 0));
}

/** Why the payment cannot be saved yet. The server refuses each of these too. */
export function paymentProblems(draft: PaymentDraft, ledger: VendorLedger | undefined): string[] {
  const problems: string[] = [];

  if (draft.vendorId === null) {
    problems.push('Choose who is being paid.');
  }

  if (draft.amount === null || draft.amount <= 0) {
    problems.push('Type how much was paid.');
  }

  if (draft.choosing && ledger) {
    for (const due of ledger.dues) {
      const value = draft.chosen[dueKey(due.kind, due.id)] ?? 0;

      if (value < 0) {
        problems.push(`${due.reference}: an amount cannot be negative.`);
      } else if (value > due.outstanding) {
        problems.push(`${due.reference}: only ${due.outstanding} is left to pay.`);
      }
    }

    if (draft.amount !== null && chosenTotal(draft) > draft.amount) {
      problems.push('The amounts set against bills and jobs come to more than the payment.');
    }
  }

  return problems;
}

export function toPaymentRequest(
  draft: PaymentDraft,
  ledger: VendorLedger | undefined,
): SaveVendorPaymentInput {
  return {
    vendorId: draft.vendorId ?? 0,
    paidOn: draft.paidOn,
    amount: draft.amount ?? 0,
    method: draft.method,
    reference: draft.reference.trim() || null,
    notes: draft.notes.trim() || null,
    allocations:
      draft.choosing && ledger
        ? ledger.dues
            .map((due) => ({
              kind: due.kind,
              targetId: due.id,
              amount: draft.chosen[dueKey(due.kind, due.id)] ?? 0,
            }))
            .filter((part) => part.amount > 0)
        : null,
    cashAccountId: draft.cashAccountId,
  };
}
